import type { PublicationCode } from '@/scopes/common/domain/types'
import type { SearchFilters } from '@/scopes/search/search/infrastructure/api/types'
import { useCMSPageField } from '@/cms/fields'
import { type DateOrRange, departureDateStore } from '@/infrastructure/departure-date/store'
import { Custom500 } from '@/pages/500'
import { Footer } from '@/scopes/common/components'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { roomsParticipantsDistributionStore } from '@/scopes/common/components/Filters/Participants/store'
import { SummerContent } from '@/scopes/common/components/Footer/SummerContent'
import { Header } from '@/scopes/common/components/Header'
import { OptimizelyFlagProvider } from '@/scopes/common/infrastructure/ab-testing/useOptimizely'
import { useCatalogueConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useCommonDependency } from '@/scopes/common/infrastructure/dependencies/common'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import { createBrowserQueryStringKeepingParams } from '@/scopes/common/infrastructure/query-strings/browserQueryStringKeepingParams'
import { QuickSearchContentLegacy } from '@/scopes/search/components/QuickSearchBarLegacy/QuickSearchContentLegacy'
import { filterSelectionQueryParamHandler as createFilterSelectionQueryParamHandler } from '@/scopes/search/search/domain/filterSelectionQueryParamHandler'
import { useAIFilterQuery, useResults } from '@/scopes/search/search/domain/hooks'
import { getDefaultFilterValues } from '@/scopes/search/search/domain/policies/publicationCodes'
import { createQueryStringToSearchStateMapper } from '@/scopes/search/search/domain/queryStringToSearchStateMapper'
import { createSearchApiClient } from '@/scopes/search/search/infrastructure/api/searchApiClient'
import { createCampaignDetailsApiClient } from '@/scopes/search/search/infrastructure/campaign-details/campaignDetailsApiClient'
import { searchQueryClient } from '@/scopes/search/search/infrastructure/query-client/searchQueryClient'
import { searchStore } from '@/scopes/search/search/infrastructure/store/store'
import {
  DepartureDateQuickSearchItemFilter,
} from '@/scopes/search/search/pages/SearchPage/components/QuickSearchFilters/DepartureDateFilter'

import {
  DurationQuickSearchItemFilter,
} from '@/scopes/search/search/pages/SearchPage/components/QuickSearchFilters/DurationFilter'
import { getFlexibleRangeForDepartureDate } from '@/scopes/search/search/pages/SearchPage/components/QuickSearchFiltersLegacy/DepartureDateFilter/utils'
import { SideBar } from '@/scopes/search/search/pages/SearchPage/components/SideBar'
import { searchPassThroughParams, type SearchPassThroughParams } from '@/scopes/search/types'
import { useDecision } from '@optimizely/react-sdk'
import { LoadingSpinner, mqMin, space, tk, tok, tokPx } from '@sunwebgroup/ui-components'
import { Layout } from '@sunwebgroup/ui-components/uds'
import { QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { styled } from 'styled-components'
import { QuickSearchBar } from '../../../components/QuickSearchBar'
import { QuickSearchBarLegacy } from '../../../components/QuickSearchBarLegacy'
import { FiltersModal } from './components/FiltersModal'
import { FiltersModalLegacy } from './components/FiltersModalLegacy'
import { Hero } from './components/Hero'
import { HeroLegacy } from './components/HeroLegacy'
import { DepartureAirportQuickSearchItemFilterLegacy } from './components/QuickSearchFiltersLegacy/DepartureAiportFilterLegacy'
import { DepartureDateQuickSearchItemFilterLegacy } from './components/QuickSearchFiltersLegacy/DepartureDateFilter'
import { DurationQuickSearchItemFilterLegacy } from './components/QuickSearchFiltersLegacy/DurationFilterLegacy'
import { ParticipantsQuickSearchItemFilterLegacy } from './components/QuickSearchFiltersLegacy/ParticipantsFilterLegacy'
import { Results } from './components/Results'
import { SideBarLegacy } from './components/SideBarLegacy'
import { SearchDependenciesProvider, useSearchDependencies } from './context'

const StyledLoadingSpinnerLegacy = styled(LoadingSpinner)`
  margin-top: 80px;
`
const StyledLoadingSpinner = styled(LoadingSpinner)`
  margin-top: 80px;
  grid-column: span 2;
`
const Wrapper = styled.div`
  background-color: ${tok('semantics_views_tinted_background_color')};
  min-height: 100vh;
`

const Content = styled(Layout)`
  display: grid;
  grid-template-columns: 100%;
  gap: ${space('primitives_space_24')};
  padding-top: ${tokPx('semantics_layouts_s_margin_top_and_bottom')};
  padding-bottom: ${tokPx('semantics_layouts_s_margin_top_and_bottom')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    padding-top: ${tokPx('semantics_layouts_m_margin_top_and_bottom')};
    padding-bottom: ${tokPx('semantics_layouts_m_margin_top_and_bottom')};
  }
  @media ${mqMin('primitives_breakpoint_l_min')} {
    padding-top: 0;
    padding-bottom: 0;
    grid-template-columns: minmax(303px, 383px) minmax(600px, auto);
  }

  ${StyledLoadingSpinner} {
    align-self: center;
    justify-self: center;
  }
`

const ContentLegacy = styled.div`
  display: flex;
  position: relative;
  justify-content: center;

  max-width: ${tk('breakpoint_desktop_xl')};
  margin: auto;
`

function Search() {
  useAIFilterQuery()
  const { filterSelectionQueryParamHandler } = useSearchDependencies()
  const { isLoading, isFetching } = useResults()
  const publicationCode = useCatalogueConfiguration('summer', 'publicationCode')
  const ACTIVATE_UDS_SEARCH = useFeatureFlag('ACTIVATE_UDS_SEARCH')
  const ACTIVATE_UDS_SEARCH_QUICKSEARCH = useFeatureFlag('ACTIVATE_UDS_SEARCH_QUICKSEARCH')

  React.useEffect(() => {
    const defaultValues = getDefaultFilterValues(publicationCode as PublicationCode) // TODO change this `as` when configurations are complete
    const queryStringToSearchStateMapper = createQueryStringToSearchStateMapper({ defaultValues })
    const initialState = queryStringToSearchStateMapper.map(window.location.search)

    searchStore.setState(initialState)
    filterSelectionQueryParamHandler.update()

    if (initialState.filterSelection?.DepartureDate) {
      const departureDate = initialState.filterSelection?.DepartureDate as DateOrRange

      departureDateStore.setState({
        departureDate,
        flexibleRange: getFlexibleRangeForDepartureDate(departureDate),
        originDate: '',
        view: 'calendar',
      })
    }
    if (initialState.filterSelection?.Participants) {
      roomsParticipantsDistributionStore.setState({
        roomsParticipantDistribution: initialState.filterSelection?.Participants,
      })
    }
  }, [])

  return (
    <NewRelicErrorBoundary scope="search-page" fallback={<Custom500 brand="sunweb" />}>
      <Wrapper>
        {ACTIVATE_UDS_SEARCH_QUICKSEARCH
          ? (
              <Hero>
                {!isLoading && (
                  <QuickSearchBar disabled={isFetching}>
                    <DepartureDateQuickSearchItemFilter />
                    <DurationQuickSearchItemFilter />
                  </QuickSearchBar>
                )}
              </Hero>
            )
          : (
              <HeroLegacy>
                {!isLoading && (
                  <QuickSearchBarLegacy disabled={isFetching}>
                    <QuickSearchContentLegacy disabled={isFetching}>
                      <DepartureDateQuickSearchItemFilterLegacy />
                      <DurationQuickSearchItemFilterLegacy />
                      <ParticipantsQuickSearchItemFilterLegacy />
                      <DepartureAirportQuickSearchItemFilterLegacy />
                    </QuickSearchContentLegacy>
                  </QuickSearchBarLegacy>
                )}
              </HeroLegacy>
            )}

        {
          ACTIVATE_UDS_SEARCH
            ? (
                <Content>
                  {isLoading
                    ? (
                        <StyledLoadingSpinner />
                      )
                    : (
                        <>
                          <SideBar />
                          <Results />
                          <FiltersModal />
                        </>
                      )}
                </Content>
              )
            : (
                <ContentLegacy>
                  {isLoading
                    ? (
                        <StyledLoadingSpinnerLegacy />
                      )
                    : (
                        <>
                          <SideBarLegacy />
                          <Results />
                          <FiltersModalLegacy />
                        </>
                      )}

                </ContentLegacy>
              )
        }
      </Wrapper>
    </NewRelicErrorBoundary>
  )
}

const SearchFooter = styled(Footer)`
  background-color: ${tok('semantics_views_tinted_background_color')};
`

function Page() {
  const [productAdsBannerDecision] = useDecision('ID_399__add_a_p195_banner_to_the_lister')
  const [hideCarouselDecision] = useDecision('ID_349_hide_carousel')
  const contextItemId = useCMSPageField('contextItemId')
  const queryStringClient = useCommonDependency('queryStringClient')

  const browserQuery = createBrowserQueryStringKeepingParams<SearchPassThroughParams>({
    passThroughParams: searchPassThroughParams,
    queryStringClient,
  })

  return (
    <SearchDependenciesProvider
      dependencies={{
        filterSelectionQueryParamHandler: createFilterSelectionQueryParamHandler({
          store: searchStore,
          browserQuery,
        }),
        searchAPIClient: createSearchApiClient<SearchFilters>({
          contextItemId,
          browserQuery,
        }),
        campaignDetailsApiClient: createCampaignDetailsApiClient({ contextItemId }),
      }}
    >
      <OptimizelyFlagProvider
        value={{
          productAdsBanner: productAdsBannerDecision.enabled,
          hideCarousel: hideCarouselDecision.enabled,
        }}
      >
        <QueryClientProvider client={searchQueryClient}>
          <Header />
          <Search />
          <SearchFooter>
            <SummerContent />
          </SearchFooter>
          <DevTools />
        </QueryClientProvider>
      </OptimizelyFlagProvider>
    </SearchDependenciesProvider>
  )
}

export default Page
